import { KeycloakAuth } from 'src/config/auth/KeycloakAuth.ts'
import { SCHOOL_PROPERTIES } from 'src/config/constants.ts'

// A completed step-up is valid until the next 04:00 (local time)
export const AUTH_RESET_HOUR = 4

const STEP_UP_DONE_KEY_PREFIX = 'kc-step-up-done:'
const STEP_UP_RETURNED_KEY = 'kc-step-up-returned-at'
// If the API still demands a step-up this soon after returning from Keycloak, the token never got acr 2
const STEP_UP_RETURNED_GUARD_MS = 60 * 1000

/**
 * Remembers the moment the user came back from the step-up login (per tab). Used by the axios interceptor
 * to avoid redirecting to Keycloak in a loop when the backend keeps answering `step_up_required`.
 */
export function markStepUpReturned(now = Date.now()) {
  try {
    sessionStorage.setItem(STEP_UP_RETURNED_KEY, String(now))
  } catch {
    // storage unavailable: no loop protection, the step-up itself still works
  }
}

export function wasStepUpReturnedRecently(now = Date.now()) {
  try {
    const returnedAt = Number(sessionStorage.getItem(STEP_UP_RETURNED_KEY))
    return Boolean(returnedAt) && now - returnedAt < STEP_UP_RETURNED_GUARD_MS
  } catch {
    return false
  }
}

/**
 * Epoch milliseconds of the most recent AUTH_RESET_HOUR:00 (today if already passed, otherwise yesterday).
 */
export function getLastAuthResetMs(now = new Date()) {
  const reset = new Date(now)
  reset.setHours(AUTH_RESET_HOUR, 0, 0, 0)
  if (reset > now) reset.setDate(reset.getDate() - 1)
  return reset.getTime()
}

/**
 * Remembers that the user completed the step-up login. Called by OidcCallback after returning from Keycloak.
 */
export function markStepUpDone(sub, now = Date.now()) {
  if (!sub) return
  try {
    localStorage.setItem(`${STEP_UP_DONE_KEY_PREFIX}${sub}`, String(now))
  } catch {
    // storage unavailable: the user will simply be asked again
  }
}

/**
 * Step-up is only required for schools where it is enabled in IAM (school property iam.step_up_auth.enabled).
 * The property can arrive as a boolean or as a JSON boolean string.
 */
function isStepUpEnabledForSchool(store) {
  const value = store?.getters?.['users/me']?.school?.properties?.[SCHOOL_PROPERTIES.SCHOOL_PROPERTY_STEP_UP_AUTH]
  return value === true || value === 'true' || value === 1 || value === '1'
}

function isStepUpDone(sub, now = new Date()) {
  try {
    const doneAt = Number(localStorage.getItem(`${STEP_UP_DONE_KEY_PREFIX}${sub}`))
    return Boolean(doneAt) && doneAt >= getLastAuthResetMs(now)
  } catch {
    return false
  }
}

/**
 * For sensitive pages (Digital Classbook, exports): the first entry after 04:00 requires a fresh Keycloak login
 * (acr_values=2, prompt=login), even right after the regular login. The completed step-up is then valid until
 * the next 04:00. Only applies to schools with iam.step_up_auth.enabled; non-Keycloak sessions are not affected.
 */
export default async function recentAuth({ to, store }) {
  if (!isStepUpEnabledForSchool(store)) return

  const realm = await KeycloakAuth.getStoredRealm()
  if (!realm) return

  const kcAuth = await KeycloakAuth.getInstance(realm)
  const sub = (await kcAuth?.getUser())?.profile?.sub
  if (!sub || isStepUpDone(sub)) return

  await kcAuth.signinRedirectStepUp(to.fullPath)
  // Abort in-app navigation, the browser is leaving for Keycloak
  return false
}
